import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Alert, Box, Button, Group, Loader, Text } from '@mantine/core';
import { AlertTriangle, ReceiptText } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { LicenseCostPageViewModel } from '@/features/licenses/LicenseCostPageViewModel';
import SeatCostDrawer from '@/features/licenses/SeatCostDrawer';
import SpendCostRail from './SpendCostRail';
import SpendCostRankedList from './SpendCostRankedList';
import { buildProviderCostRows } from './SpendCostRows';

/**
 * Spend › Cost landing — Cost by Provider. A ranked list of provider connections
 * by hard-dollar spend, each expandable to its by-model breakdown (with a
 * "Seats & subscriptions" bucket for flat costs). Rows drill into the underlying
 * cost records. Scoped by the global date range + provider filter.
 */
export default function SpendCostByProviderPage(): ReactElement {
    const vm = useViewModel(LicenseCostPageViewModel);
    const surface = useSurface('token-usage');
    const { snapshot } = useGlobalFilters();
    const [seatDrawerOpen, setSeatDrawerOpen] = useState(false);
    const filterKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

    useEffect(() => {
        void vm.reload();
    }, [vm, filterKey]);

    if (surface === undefined) return <TopicNotFound />;

    const rows = buildProviderCostRows(vm.providerCosts, vm.grandTotal);

    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <PageHeaderComponent
                    icon={ReceiptText}
                    eyebrow="Cost"
                    title="Cost by Provider"
                    subtitle="Hard-dollar spend by provider connection — expand a provider to see spend by model, or open one to read its cost records."
                    rightToolbar={
                        <Button
                            size="xs"
                            variant="default"
                            leftSection={<ReceiptText size={14} />}
                            onClick={() => {
                                setSeatDrawerOpen(true);
                                void vm.loadSeatCostHistory();
                            }}
                        >
                            Record seat cost
                        </Button>
                    }
                />

                <SeatCostDrawer vm={vm} opened={seatDrawerOpen} onClose={() => setSeatDrawerOpen(false)} />

                {vm.loading ?
                    <Group justify="center" py={64}>
                        <Loader size="sm" />
                    </Group>
                : vm.loadError ?
                    <Alert color="red" icon={<AlertTriangle size={16} />} title="Could not load cost data">
                        {vm.loadError}
                    </Alert>
                : rows.length === 0 ?
                    <Text size="sm" c="dimmed">
                        No cost records in the selected range.
                    </Text>
                :   <SideRailPageLayout rail={<SpendCostRail vm={vm} scopeKey={filterKey} />}>
                        <SpendCostRankedList rows={rows} />
                    </SideRailPageLayout>
                }
            </Box>
        </SurfaceAssistScope>
    );
}
